import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { usePermissions as usePermissionsContext } from '../contexts/PermissionsContext';

/**
 * Hook to calculate user permissions for organization, team, and project contexts
 * Uses the new PermissionsContext for role resolution
 * 
 * @param {Object} project - Optional project object
 * @param {Object} team - Optional team object
 * @returns {Object} Permission flags and user roles
 */
export const usePermissions = (project = null, team = null) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { 
    hasProjectPermission, 
    getProjectRole, 
    hasProjectAccess
  } = usePermissionsContext();
  
  const permissions = useMemo(() => {
    if (!user) {
      return {
        // Organization permissions
        canViewOrg: false,
        canManageOrg: false,
        canInviteMembers: false,
        canCreateTeams: false,
        canCreateProjects: false,
        canAccessBilling: false,

        // Project permissions
        canViewProject: false,
        canEditProject: false,
        canManageProject: false,
        canDeleteProject: false,
        canManageMembers: false,

        // Team permissions
        canViewTeam: false,
        canManageTeam: false,
        canAddTeamMembers: false,
        canAssignProjects: false,

        // Roles
        userOrgRole: null,
        userProjectRole: null,
        userTeamRole: null,
        isOrgOwner: false,
        isOrgAdmin: false,
        isProjectMaintainer: false,
        isTeamLead: false
      };
    }

    // Get org role — read directly from currentOrganization.role which is
    // populated by the startup context (AppStartupOrg.role). This is the
    // authoritative source; userWithOrgs is not used and owner_id is not
    // included in the startup context schema.
    const userOrgRole = currentOrganization?.role || null;

    const isOrgOwner = userOrgRole === 'owner';
    const isOrgAdmin = ['owner', 'admin'].includes(userOrgRole);

    // Get project role using the permissions context — works for both org and
    // personal projects (no org required).
    let userProjectRole = null;
    let isProjectMaintainer = false;
    let canViewProject = false;
    let canEditProject = false;
    let canManageProject = false;

    if (project) {
      userProjectRole = getProjectRole(project.id);
      isProjectMaintainer = userProjectRole === 'maintainer';

      canViewProject = hasProjectAccess(project.id) || project?.is_public;
      canEditProject = hasProjectPermission(project.id, 'contributor');
      canManageProject = hasProjectPermission(project.id, 'maintainer');
    }
    
    // Get team role (keeping existing logic for now)
    let userTeamRole = null;
    let isTeamLead = false;
    if (team) {
      const teamMember = team.members?.find(m => m.user_id === user.id);
      userTeamRole = teamMember?.role || null;
      isTeamLead = userTeamRole === 'lead';
    }
    
    return {
      // Organization permissions
      canViewOrg: !!userOrgRole,
      canManageOrg: isOrgAdmin,
      canInviteMembers: isOrgAdmin,
      canCreateTeams: isOrgAdmin,
      canCreateProjects: isOrgAdmin,
      canAccessBilling: isOrgOwner,
      
      // Project permissions (using new permissions system)
      canViewProject,
      canEditProject,
      canManageProject,
      canDeleteProject: canManageProject, // Same as manage for now
      canManageMembers: canManageProject,
      
      // Team permissions
      canViewTeam: isOrgAdmin || !!userTeamRole,
      canManageTeam: isOrgAdmin || isTeamLead,
      canAddTeamMembers: isOrgAdmin || isTeamLead,
      canAssignProjects: isOrgAdmin || isTeamLead,
      
      // Roles
      userOrgRole,
      userProjectRole,
      userTeamRole,
      isOrgOwner,
      isOrgAdmin,
      isProjectMaintainer,
      isTeamLead
    };
  }, [user, currentOrganization, project, team, hasProjectPermission, getProjectRole, hasProjectAccess]);
  
  return permissions;
};

export default usePermissions;
